import { useLocation } from 'react-router-dom';

import { BaseConfigItems } from '@Enum/index';
import ESSFeatureRoutePath from '@Features/router/ess/ESSFeatureRoutePath.enum';
import MRFeatureRoutePath from '@Features/router/mr/MRFeatureRoutePath.enum';
import { ConfigItem } from '@Types/index';
import {
  getMREnable,
  getContactPageEnable,
  getRaiseRequestMenuEnable,
  getReportIssueMenuEnable,
} from '@Utils/getFeatureFlag';
import BaseRoutePath from '@Router/baseRoutePathes.enum';

// need to make feature flag for DEV env
type OptionalProp = Partial<{
  [key in BaseConfigItems]: ConfigItem;
}>;
// type ConfigItems = Record<BaseConfigItems, ConfigItem>;

interface CurrentMenuListProps {
  isMREnable: boolean;
  isContactPageEnable: boolean;
  isReportIssueMenuEnable: boolean;
  isRaiseRequestMenuEnable: boolean;
  currentMenuConfig: Record<string, ConfigItem>;
}

const configItems: OptionalProp = {
  [BaseConfigItems.ESS]: {
    title: BaseConfigItems.ESS,
    link: `/ess/${ESSFeatureRoutePath.MANAGE_REQUESTS}/${ESSFeatureRoutePath.MANAGE_REQUEST_LEAVE_REQUEST}`,
    selected: false,
  },
  // TODO: Hide MR request by 1631 task
  // [BaseConfigItems.MR]: {
  //   title: BaseConfigItems.MR,
  //   link: `/mr/${MRFeatureRoutePath.MANAGE_REQUESTS}/${MRFeatureRoutePath.MANAGE_REQUEST_MATERIAL_REQUEST}`,
  //   selected: false,
  // },
  [BaseConfigItems.DASHBOARD]: {
    title: BaseConfigItems.DASHBOARD,
    link: '/',
    selected: false,
  },
  // TODO: Hide Items by 1895 task
  // [BaseConfigItems.CONTACTS]: {
  //   title: BaseConfigItems.CONTACTS,
  //   link: BaseRoutePath.CONTACTS,
  //   selected: false,
  // },
  [BaseConfigItems.TBMS]: {
    title: BaseConfigItems.TBMS,
    link: '',
    onClick: undefined,
    selected: false,
  },
  // [BaseConfigItems.REPORT_ISSUE]: {
  //   title: BaseConfigItems.REPORT_ISSUE,
  //   link: `https://itservices.trojanconstruction.group/MDLIncidentMgmt/IM_LogTicket.aspx`,
  //   selected: false,
  // },
  // [BaseConfigItems.RAISE_REQUEST]: {
  //   title: BaseConfigItems.RAISE_REQUEST,
  //   link: `https://itservices.trojanconstruction.group/MDLServiceMgmt/SR_LogServiceTicket.aspx`,
  //   selected: false,
  // },
};

const getCurrentMenuConfig = (
  currentMenuItem: BaseConfigItems
): Record<string, ConfigItem> => {
  const isMREnable = getMREnable();
  const isContactPageEnable = getContactPageEnable();
  const isRaiseRequestMenuEnable = getRaiseRequestMenuEnable();
  const isReportIssueMenuEnable = getReportIssueMenuEnable();

  if (isMREnable) {
    configItems[BaseConfigItems.MR] = {
      title: BaseConfigItems.MR,
      link: `/mr/${MRFeatureRoutePath.MANAGE_REQUESTS}/${MRFeatureRoutePath.MANAGE_REQUEST_MATERIAL_REQUEST}`,
      selected: false,
    };
  }

  if (isContactPageEnable) {
    configItems[BaseConfigItems.CONTACTS] = {
      title: BaseConfigItems.CONTACTS,
      link: BaseRoutePath.CONTACTS,
      selected: false,
    };
  }

  if (isReportIssueMenuEnable) {
    configItems[BaseConfigItems.REPORT_ISSUE] = {
      title: BaseConfigItems.REPORT_ISSUE,
      link: `https://itservices.trojanconstruction.group/MDLIncidentMgmt/IM_LogTicket.aspx`,
      selected: false,
    };
  }

  if (isRaiseRequestMenuEnable) {
    configItems[BaseConfigItems.RAISE_REQUEST] = {
      title: BaseConfigItems.RAISE_REQUEST,
      link: `https://itservices.trojanconstruction.group/MDLServiceMgmt/SR_LogServiceTicket.aspx`,
      selected: false,
    };
  }

  const configCopy = structuredClone(configItems);

  if (configCopy[currentMenuItem] !== undefined) {
    configCopy[currentMenuItem]!.selected = true;
  }

  return configCopy;
};

const getCurrentMenuItem = (
  isEmployeeSelfService: boolean,
  isMaterialRequest?: boolean,
  isContacts?: boolean
): BaseConfigItems => {
  if (isEmployeeSelfService) {
    return BaseConfigItems.ESS;
  }

  if (isMaterialRequest) {
    return BaseConfigItems.MR;
  }

  if (isContacts) {
    return BaseConfigItems.CONTACTS;
  }

  return BaseConfigItems.DASHBOARD;
};

const getEnabledMenuList = ({
  isMREnable,
  isContactPageEnable,
  isRaiseRequestMenuEnable,
  isReportIssueMenuEnable,
  currentMenuConfig,
}: CurrentMenuListProps): ConfigItem[] => {
  const menuList = [
    currentMenuConfig[BaseConfigItems.DASHBOARD],
    currentMenuConfig[BaseConfigItems.ESS],
    isMREnable ? currentMenuConfig[BaseConfigItems.MR] : [],
    isContactPageEnable ? currentMenuConfig[BaseConfigItems.CONTACTS] : [],
    currentMenuConfig[BaseConfigItems.TBMS],
    isReportIssueMenuEnable
      ? currentMenuConfig[BaseConfigItems.REPORT_ISSUE]
      : [],
    isRaiseRequestMenuEnable
      ? currentMenuConfig[BaseConfigItems.RAISE_REQUEST]
      : [],
  ];

  return menuList.flat();
};

const useAppsMenuConfig = (): ConfigItem[] => {
  const { pathname } = useLocation();
  const isMREnable = getMREnable();
  const isContactPageEnable = getContactPageEnable();
  const isRaiseRequestMenuEnable = getRaiseRequestMenuEnable();
  const isReportIssueMenuEnable = getReportIssueMenuEnable();

  const isEmployeeSelfService = pathname.includes('/ess/');
  const isMaterialRequest = pathname.includes('/mr/');
  const isContacts = pathname.includes('/contacts');

  const currentMenuItem = getCurrentMenuItem(
    isEmployeeSelfService,
    isMREnable ? isMaterialRequest : undefined,
    isContactPageEnable ? isContacts : undefined
  );

  const currentMenuConfig = getCurrentMenuConfig(currentMenuItem);

  const menuItemList = getEnabledMenuList({
    isMREnable,
    isContactPageEnable,
    isRaiseRequestMenuEnable,
    isReportIssueMenuEnable,
    currentMenuConfig,
  });

  return menuItemList;
};

export default useAppsMenuConfig;
